/**
 * A differentiable 2-D vector: a pair of {@link Value}s that flows gradient
 * through every geometric operation the kinematics layer performs (rotation,
 * distance, cross products, rigid-body transforms).
 *
 * `Vec2` is immutable — every method returns a new vector — so the computation
 * graph stays a clean DAG.
 */

import { Value, V, hypot, type Num } from "./value.js";

export class Vec2 {
  readonly x: Value;
  readonly y: Value;

  constructor(x: Num, y: Num) {
    this.x = V(x);
    this.y = V(y);
  }

  static of(x: Num, y: Num): Vec2 {
    return new Vec2(x, y);
  }

  /** Unit vector at angle `a` (radians), optionally scaled by `r`. */
  static fromAngle(a: Num, r: Num = 1): Vec2 {
    const av = V(a);
    return new Vec2(av.cos().mul(r), av.sin().mul(r));
  }

  static zero(): Vec2 {
    return new Vec2(0, 0);
  }

  add(o: Vec2): Vec2 {
    return new Vec2(this.x.add(o.x), this.y.add(o.y));
  }

  sub(o: Vec2): Vec2 {
    return new Vec2(this.x.sub(o.x), this.y.sub(o.y));
  }

  /** Scale by a scalar (number or Value). */
  scale(s: Num): Vec2 {
    return new Vec2(this.x.mul(s), this.y.mul(s));
  }

  neg(): Vec2 {
    return new Vec2(this.x.neg(), this.y.neg());
  }

  dot(o: Vec2): Value {
    return this.x.mul(o.x).add(this.y.mul(o.y));
  }

  /** Scalar (z-component) cross product `this × o`. */
  cross(o: Vec2): Value {
    return this.x.mul(o.y).sub(this.y.mul(o.x));
  }

  /** Euclidean length, numerically guarded away from the non-smooth origin. */
  norm(): Value {
    return hypot(this.x, this.y);
  }

  /** Squared length (smooth everywhere, cheaper). */
  normSq(): Value {
    return this.x.square().add(this.y.square());
  }

  dist(o: Vec2): Value {
    return this.sub(o).norm();
  }

  distSq(o: Vec2): Value {
    return this.sub(o).normSq();
  }

  /** Rotate by angle `a` (radians) about the origin. */
  rotate(a: Num): Vec2 {
    const av = V(a);
    const c = av.cos();
    const s = av.sin();
    return new Vec2(this.x.mul(c).sub(this.y.mul(s)), this.x.mul(s).add(this.y.mul(c)));
  }

  /** Rotate about pivot `p` by angle `a`. */
  rotateAround(p: Vec2, a: Num): Vec2 {
    return this.sub(p).rotate(a).add(p);
  }

  /** Left-hand perpendicular `(-y, x)`. */
  perp(): Vec2 {
    return new Vec2(this.y.neg(), this.x);
  }

  /** Unit vector in the same direction (guarded). */
  unit(): Vec2 {
    const n = this.norm();
    return new Vec2(this.x.div(n), this.y.div(n));
  }

  /** Signed angle of the vector, `atan2(y, x)`. */
  angle(): Value {
    return this.y.atan2(this.x);
  }

  /** Linear interpolation toward `o` by `t` in [0,1]. */
  lerp(o: Vec2, t: Num): Vec2 {
    return this.add(o.sub(this).scale(t));
  }

  /** Detach to plain numbers (drops gradient linkage). */
  toArray(): [number, number] {
    return [this.x.data, this.y.data];
  }

  toString(): string {
    return `Vec2(${this.x.data.toFixed(4)}, ${this.y.data.toFixed(4)})`;
  }
}

/** Convenience constructor. */
export const vec2 = (x: Num, y: Num): Vec2 => new Vec2(x, y);
